import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import {
  nflMatchupsQueryKey, fetchNflMatchups,
  nflSabotageEventsQueryKey, fetchNflSabotageEvents,
} from '@/lib/nflSharedQueries';
import { useNFLLeague } from '@/contexts/NFLLeagueContext';
import { useAuth } from '@/contexts/AuthContext';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import {
  ChevronLeft, Wallet, Store, Swords, Target, Clock, AlertTriangle,
  CheckCircle2, Loader2, Bomb, ShoppingCart,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { trackEvent } from '@/lib/analytics';
import { useLocanceGeoCheck } from '@/hooks/useLocanceGeoCheck';
import { GEO_ENABLED } from '@/lib/locance';
import { acquireGeoSession } from '@/lib/acquireGeoSession';
import { mapDepositError } from '@/lib/depositErrors';
import SabotageDeployDialog, { DeployContext } from './SabotageDeployDialog';

/* ------------------------------------------------------------------ */
/*  Types                                                             */
/* ------------------------------------------------------------------ */

interface SabotageCard {
  id: string;
  name: string;
  description: string | null;
  category: string;
  rarity: string;
  cost: number;
  price_usd_cents: number | null;
  effect_type: string | null;
  effect_params: any;
  emoji: string | null;
  target_scope: string;
  is_active: boolean;
}

interface InventoryItem {
  id: string;
  card_id: string;
  is_used: boolean;
  acquired_at: string;
  matchup_id: string | null;
  expires_at: string | null;
}

interface SabotageEvent {
  id: string;
  card_id: string;
  attacker_team_id: string;
  target_team_id: string | null;
  week: number;
  status: string;
}

interface Matchup {
  id: string;
  home_team_id: string;
  away_team_id: string;
  week: number;
  status: string;
}

/* ------------------------------------------------------------------ */
/*  Rarity styling (design tokens: common gray, rare blue, legendary  */
/*  purple)                                                           */
/* ------------------------------------------------------------------ */

const RARITY_ORDER = ['common', 'rare', 'legendary'] as const;

const RARITY = {
  common: {
    label: 'Common',
    text: 'text-[hsl(215,20%,65%)]',
    swatch: 'bg-[hsl(215,20%,55%)]',
    border: 'border-[hsl(215,20%,55%/0.3)]',
    tile: 'bg-card',
    chip: 'bg-[hsl(215,20%,55%/0.12)] border-[hsl(215,20%,55%/0.4)]',
    badge: 'bg-[hsl(215,20%,55%/0.18)] text-[hsl(215,20%,70%)] border-[hsl(215,20%,55%/0.4)]',
    glow: '',
  },
  rare: {
    label: 'Rare',
    text: 'text-[hsl(220,90%,66%)]',
    swatch: 'bg-[hsl(220,90%,56%)]',
    border: 'border-[hsl(220,90%,56%/0.4)]',
    tile: 'bg-[hsl(220,90%,56%/0.06)]',
    chip: 'bg-[hsl(220,90%,56%/0.14)] border-[hsl(220,90%,56%/0.45)]',
    badge: 'bg-[hsl(220,90%,56%/0.18)] text-[hsl(220,90%,70%)] border-[hsl(220,90%,56%/0.45)]',
    glow: 'shadow-[0_0_24px_hsl(220,90%,56%/0.12)]',
  },
  legendary: {
    label: 'Legendary',
    text: 'text-[hsl(280,85%,70%)]',
    swatch: 'bg-[hsl(280,85%,60%)]',
    border: 'border-[hsl(280,85%,60%/0.45)]',
    tile: 'bg-[hsl(280,85%,60%/0.07)]',
    chip: 'bg-[hsl(280,85%,60%/0.16)] border-[hsl(280,85%,60%/0.5)]',
    badge: 'bg-[hsl(280,85%,60%/0.2)] text-[hsl(280,85%,72%)] border-[hsl(280,85%,60%/0.5)]',
    glow: 'shadow-[0_0_30px_hsl(280,85%,60%/0.16)]',
  },
} as const;

type RarityKey = keyof typeof RARITY;
const rarityOf = (r: string): RarityKey => (r in RARITY ? (r as RarityKey) : 'common');

const usd = (cents: number | null | undefined) =>
  cents == null ? '' : `$${(cents / 100).toFixed(2)}`;

/* ------------------------------------------------------------------ */
/*  Live countdown to matchup lock                                    */
/* ------------------------------------------------------------------ */

function useCountdown(target: string | null): string {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!target) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [target]);
  if (!target) return 'TBD';
  let ms = new Date(target).getTime() - now;
  if (ms <= 0) return 'Locked';
  const d = Math.floor(ms / 86400000); ms -= d * 86400000;
  const h = Math.floor(ms / 3600000); ms -= h * 3600000;
  const m = Math.floor(ms / 60000); ms -= m * 60000;
  const s = Math.floor(ms / 1000);
  const pad = (n: number) => String(n).padStart(2, '0');
  return d > 0 ? `${d}d ${pad(h)}h ${pad(m)}m ${pad(s)}s` : `${pad(h)}h ${pad(m)}m ${pad(s)}s`;
}

const shortExpiry = (target: string | null): string => {
  if (!target) return '';
  let ms = new Date(target).getTime() - Date.now();
  if (ms <= 0) return 'locked';
  const d = Math.floor(ms / 86400000); const h = Math.floor((ms % 86400000) / 3600000);
  return d > 0 ? `${d}d ${h}h` : `${h}h`;
};

/* ------------------------------------------------------------------ */
/*  Small building blocks                                             */
/* ------------------------------------------------------------------ */

function ArtChip({ emoji, r, size = 44 }: { emoji: string | null; r: RarityKey; size?: number }) {
  return (
    <span
      className={cn('shrink-0 rounded-xl flex items-center justify-center border', RARITY[r].chip, RARITY[r].glow)}
      style={{ width: size, height: size, fontSize: size * 0.5 }}
    >
      {emoji || '💣'}
    </span>
  );
}

function Scrim({ onClose, children }: { onClose: () => void; children: React.ReactNode }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[rgba(3,5,10,0.74)] backdrop-blur-sm animate-in fade-in duration-150"
      onClick={onClose}
    >
      <div
        className="w-[min(94vw,440px)] rounded-[22px] bg-[hsl(222,47%,10%)] border border-border p-5 shadow-[0_30px_70px_rgba(0,0,0,0.6)] animate-in zoom-in-95 duration-200"
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Component                                                         */
/* ------------------------------------------------------------------ */

type ModalKind = 'none' | 'detail' | 'confirm' | 'success' | 'deploy' | 'deploySuccess';

export default function NFLSabotagesPage() {
  const { currentLeague, currentTeam, currentWeek, season, teams } = useNFLLeague();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  // SAB-600: a sabotage purchase is an in-game money spend, so it must acquire a
  // FRESH Locance session at buy time and hand geo_session_id to nfl-buy-sabotage,
  // exactly like the deposit surfaces. Gated by GEO_ENABLED (OFF short-circuits —
  // no gather, no session — same as before the gate existed).
  const geo = useLocanceGeoCheck();

  const isOnDemoRoute = typeof window !== 'undefined' && window.location.pathname.includes('/nfldemo');
  const isDemoMode = !user || isOnDemoRoute;

  const queryClient = useQueryClient();

  // Matchups + active events for the week come from the SHARED cache keys (same rows Matchups
  // reads), so hopping between the store and Matchups reuses the cache. Matchups here is the
  // store's "still-open" set (not completed).
  const { data: rawMatchups = [] } = useQuery({
    queryKey: nflMatchupsQueryKey(currentLeague?.id, currentWeek, season ?? undefined),
    enabled: !!currentLeague,
    staleTime: 0,
    refetchOnWindowFocus: true,
    queryFn: () => fetchNflMatchups(currentLeague!.id, currentWeek, season!),
  });
  const matchups = useMemo(() => rawMatchups.filter((m) => m.status !== 'completed'), [rawMatchups]);

  const { data: events = [] } = useQuery({
    queryKey: nflSabotageEventsQueryKey(currentLeague?.id, currentWeek),
    enabled: !!currentLeague,
    staleTime: 0,
    refetchOnWindowFocus: true,
    queryFn: () => fetchNflSabotageEvents(currentLeague!.id, currentWeek),
  });

  // Store-specific bundle: catalog, my inventory, wallet balance, lock time. Own key. isPending
  // gates the spinner, so a post-purchase refetch (invalidation below) never blanks the store —
  // that in-place blank was the SAB-830 defect this migration subsumes.
  const { data: store, isPending } = useQuery({
    queryKey: ['nfl-sabotages-store', currentLeague?.id, currentTeam?.id, currentWeek, user?.id],
    enabled: !!currentLeague && !!currentTeam,
    staleTime: 0,
    refetchOnWindowFocus: true,
    queryFn: async () => {
      // SAB-824: from the context, not a cast. season_year is NOT NULL and typed so.
      const seasonYear = season;
      const [cardsRes, invRes, walletRes, gamesRes] = await Promise.all([
        supabase.from('nfl_sabotage_cards').select('*').eq('is_active', true).order('price_usd_cents'),
        supabase.from('nfl_sabotage_inventory').select('id, card_id, is_used, acquired_at, matchup_id, expires_at').eq('team_id', currentTeam!.id).order('acquired_at', { ascending: false }),
        user ? supabase.from('user_wallets').select('balance').eq('user_id', user.id).maybeSingle() : Promise.resolve({ data: null } as any),
        // SAB-824: pin season_type. This query answers "when does the current week kick
        // off", and lockAt drives the store countdown plus the "expires in ..." copy on the
        // purchase and deploy modals -- a real-money surface.
        //
        // Without the type filter, season_year + week matched 38 rows on prod for 2026
        // week 1 (16 REG + 16 PRE + 6 PST), and .order('scheduled').limit(1) took the
        // EARLIEST -- a preseason game on 2026-08-13, four weeks before the real REG
        // kickoff on 2026-09-10 and already in the past. The countdown read as expired on
        // a week that had not started. This is the SAB-746 collision (PRE week N and REG
        // week N share a number) on a live deadline, not a rollover hazard.
        //
        // A sabotage card is a regular-season instrument; REG is the right slate here, the
        // same pin NFLMyTeamPage's lock slate and NFLPlayersPage's bye-week query use.
        //
        // `supabase as any`: the third chained .eq() tips the generated types past
        // TypeScript's instantiation depth limit (TS2589). Same escape hatch as
        // NFLPlayersPage:255; the row shape is asserted at the consumption site below.
        seasonYear ? (supabase as any).from('nfl_games').select('scheduled').eq('season_year', seasonYear).eq('season_type', 'REG').eq('week', currentWeek).order('scheduled', { ascending: true }).limit(1).maybeSingle() : Promise.resolve({ data: null } as any),
      ]);
      return {
        cards: (cardsRes.data as SabotageCard[]) || [],
        inventory: (invRes.data as InventoryItem[]) || [],
        walletBalance: Number((walletRes as any).data?.balance) || 0,
        lockAt: (((gamesRes as any).data as { scheduled: string | null } | null)?.scheduled ?? null) as string | null,
      };
    },
  });
  const cards = store?.cards ?? [];
  const inventory = store?.inventory ?? [];
  const walletBalance = store?.walletBalance ?? 0;
  const lockAt = store?.lockAt ?? null;

  // Refetch everything the store shows after a money/state action — background (no blank).
  const refreshSabotages = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['nfl-sabotages-store'] });
    queryClient.invalidateQueries({ queryKey: ['nfl-sabotage-events'] });
    queryClient.invalidateQueries({ queryKey: ['nfl-matchups'] });
  }, [queryClient]);

  const [screen, setScreen] = useState<'store' | 'arsenal'>('store');
  const [modal, setModal] = useState<ModalKind>('none');
  const [focusCardId, setFocusCardId] = useState<string | null>(null);
  const [deployCtx, setDeployCtx] = useState<DeployContext | null>(null);
  const [busy, setBusy] = useState(false);
  const [receipt, setReceipt] = useState<{ price: number; balance: number; name: string; emoji: string | null; r: RarityKey } | null>(null);

  const teamMap = useMemo(() => Object.fromEntries(teams.map((t) => [t.id, t.name])) as Record<string, string>, [teams]);

  useEffect(() => { trackEvent('sabotage_store_view'); }, []);

  const cardMap = useMemo(() => Object.fromEntries(cards.map((c) => [c.id, c])) as Record<string, SabotageCard>, [cards]);

  // Store = only cards with a USD price (C1: NULL price = draft / non-store).
  const storeByRarity = useMemo(() => {
    const groups: Record<RarityKey, SabotageCard[]> = { common: [], rare: [], legendary: [] };
    for (const c of cards) if (c.price_usd_cents != null) groups[rarityOf(c.rarity)].push(c);
    return groups;
  }, [cards]);

  const myMatchup = useMemo(
    () => matchups.find((m) => m.home_team_id === currentTeam?.id || m.away_team_id === currentTeam?.id),
    [matchups, currentTeam],
  );
  const opponentId = myMatchup
    ? (myMatchup.home_team_id === currentTeam?.id ? myMatchup.away_team_id : myMatchup.home_team_id)
    : null;
  const opponentName = opponentId ? teamMap[opponentId] || 'Opponent' : 'Opponent';

  const availableInventory = inventory.filter((i) => !i.is_used);
  const deployedThisWeek = events.filter((e) => e.attacker_team_id === currentTeam?.id);
  const playedThisWeek = deployedThisWeek.length > 0;       // C4: one play per user per week
  const weeklyRemaining = playedThisWeek ? 0 : 1;
  const leagueSlotTaken = events.some((e) => cardMap[e.card_id]?.target_scope === 'league');

  const ownedUnusedByCard = useMemo(() => {
    const map: Record<string, InventoryItem> = {};
    for (const i of availableInventory) if (!map[i.card_id]) map[i.card_id] = i;
    return map;
  }, [availableInventory]);
  const deployedCardIds = useMemo(() => new Set(deployedThisWeek.map((e) => e.card_id)), [deployedThisWeek]);

  const countdown = useCountdown(lockAt);

  const focusCard = focusCardId ? cardMap[focusCardId] : null;

  /* ---- actions ---- */

  const authToken = async () => (await supabase.auth.getSession()).data.session?.access_token ?? null;

  const openDetail = (cardId: string) => { setFocusCardId(cardId); setModal('detail'); };

  const handleBuy = async () => {
    if (!focusCard || !currentLeague) return;
    const isLW = focusCard.target_scope === 'league';
    if (!isLW && !myMatchup) { toast({ title: 'No matchup this week', description: 'Targeted cards need an active matchup.', variant: 'destructive' }); return; }
    setBusy(true);
    try {
      const token = await authToken();
      if (!token) { toast({ title: 'Sign in to buy', variant: 'destructive' }); return; }
      // SAB-600: acquire a fresh Locance session for the purchase and attach it as
      // geo_session_id so the SERVER re-verifies location and claims the session.
      // Gather under GEO_ENABLED OR GEO_OBSERVE (see acquireGeoSession). ok:false only
      // under GEO_ENABLED (enforce client) on a non-Compliant verdict: abort BEFORE the
      // POST (no charge) with the toast below. Under observe we send the session and let
      // the SERVER decide (no local block, no new UI). (KYC is transitively satisfied by
      // the wallet-funding deposit.)
      const geoRes = await acquireGeoSession(geo, currentLeague?.id);
      if (!geoRes.ok) {
        toast({
          title: 'Location check needed',
          description: 'You can only buy sabotages while in a state where real-money play is available.',
          variant: 'destructive',
        });
        return;
      }
      const geoSessionId = geoRes.sessionId;
      const res = await fetch(`${(supabase as any).supabaseUrl}/functions/v1/nfl-buy-sabotage`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          league_id: currentLeague.id, card_id: focusCard.id, matchup_id: isLW ? null : myMatchup!.id,
          ...(geoSessionId ? { geo_session_id: geoSessionId } : {}),
        }),
      });
      const data = await res.json();
      if (!res.ok || data.error) {
        const code: string = data.code || data.error || '';
        // MATCHUP_LOCKED is buy-specific and not a money-compliance code; keep it here.
        // Everything else (geo, RG, KYC, insufficient) via the shared money-action mapper,
        // so GEO_BLOCKED shows the SAB-605 location copy, not a buy-specific string.
        if (/MATCHUP_LOCKED/i.test(code)) {
          toast({ title: 'Store closed', description: 'This matchup has locked. The store is closed for it.', variant: 'destructive' });
          return;
        }
        const { title, message } = mapDepositError({ code: data.code, message: data.error, category: data.category, detected_state: data.detected_state });
        toast({ title, description: message, variant: 'destructive' });
        return;
      }
      trackEvent('sabotage_purchased', { rarity: focusCard.rarity, price_usd_cents: data.price_usd_cents });
      setReceipt({ price: data.price_usd_cents, balance: Number(data.new_balance) || 0, name: focusCard.name, emoji: focusCard.emoji, r: rarityOf(focusCard.rarity) });
      setModal('success');
      refreshSabotages();
    } catch (err) {
      toast({ title: 'Error', description: String(err), variant: 'destructive' });
    } finally {
      setBusy(false);
    }
  };

  // Build a deploy context for the shared SabotageDeployDialog. League-wide plays
  // through any of my matchups; targeted uses its bound matchup (fallback to mine).
  const buildDeployCtx = (inv: InventoryItem): DeployContext | null => {
    const card = cardMap[inv.card_id];
    if (!card || !currentLeague) return null;
    const matchupId = inv.matchup_id || myMatchup?.id;
    if (!matchupId) return null;
    return {
      leagueId: currentLeague.id, matchupId, opponentName, week: currentWeek, inventoryId: inv.id,
      card: { name: card.name, emoji: card.emoji, target_scope: card.target_scope, rarity: card.rarity },
    };
  };
  const openDeploy = (inv: InventoryItem) => {
    const ctx = buildDeployCtx(inv);
    if (ctx) { setModal('none'); setDeployCtx(ctx); }
    else toast({ title: 'No matchup this week', variant: 'destructive' });
  };

  /* ---- loading / empty ---- */

  // Only a genuine first load (query enabled, no cached data) blanks to a spinner; a background
  // refetch after a purchase/deploy keeps the store on screen (SAB-830).
  if (currentLeague && currentTeam && isPending) {
    return <div className="flex items-center justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;
  }
  if (!currentLeague || !currentTeam) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] text-center">
        <Bomb className="h-10 w-10 text-muted-foreground mb-4" />
        <p className="text-sm text-muted-foreground">Join a league to use the sabotage store</p>
      </div>
    );
  }

  const arsenalCount = availableInventory.length;

  return (
    <div className="max-w-[1120px] mx-auto space-y-5 pb-10">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button onClick={() => navigate(-1)} className="h-10 w-10 rounded-[11px] bg-muted flex items-center justify-center text-muted-foreground hover:text-foreground shrink-0">
          <ChevronLeft className="h-5 w-5" />
        </button>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <h1 className="font-display font-extrabold text-xl tracking-tight text-foreground truncate">
              {screen === 'store' ? 'Sabotage Card Store' : 'Your Arsenal'}
            </h1>
            <span className="text-[9px] font-display font-bold uppercase px-1.5 py-0.5 rounded text-white" style={{ background: 'var(--gradient-sabotage, linear-gradient(135deg,hsl(25 95% 53%),hsl(35 100% 50%)))' }}>NFL</span>
          </div>
          <p className="text-xs text-muted-foreground">{screen === 'store' ? 'Pick your weapon. Cause strategic chaos.' : 'Cards you own for this matchup.'}</p>
        </div>
        <div className="shrink-0 flex flex-col items-end rounded-full border px-3 py-1.5 border-[hsl(45,93%,47%/0.35)] bg-[hsl(45,93%,47%/0.1)]">
          <span className="flex items-center gap-1 text-[8.5px] font-display font-bold uppercase tracking-wider text-[hsl(45,93%,55%)]"><Wallet className="h-3 w-3" /> Wallet</span>
          <span className="font-display font-extrabold text-[15px] tabular-nums text-foreground leading-none">${walletBalance.toFixed(2)}</span>
        </div>
      </div>

      {/* Segmented nav */}
      <div className="flex gap-2">
        {([['store', 'Store', Store], ['arsenal', 'Arsenal', Swords]] as const).map(([key, label, Icon]) => (
          <button
            key={key}
            onClick={() => setScreen(key)}
            className={cn('h-[38px] px-4 rounded-[10px] flex items-center gap-2 text-[13.5px] font-display font-bold transition-colors',
              screen === key ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground')}
          >
            <Icon className="h-4 w-4" /> {label}
            {key === 'arsenal' && arsenalCount > 0 && (
              <span className={cn('ml-0.5 text-[10px] rounded-full px-1.5 py-0.5', screen === 'arsenal' ? 'bg-white/20 text-white' : 'bg-primary/20 text-primary')}>{arsenalCount}</span>
            )}
          </button>
        ))}
      </div>

      {screen === 'store' ? (
        <StoreView
          storeByRarity={storeByRarity}
          currentWeek={currentWeek}
          opponentName={opponentName}
          countdown={countdown}
          weeklyRemaining={weeklyRemaining}
          playedThisWeek={playedThisWeek}
          ownedUnusedByCard={ownedUnusedByCard}
          deployedCardIds={deployedCardIds}
          onOpen={openDetail}
        />
      ) : (
        <ArsenalView
          availableInventory={availableInventory}
          deployedThisWeek={deployedThisWeek}
          cardMap={cardMap}
          teamMap={teamMap}
          countdown={countdown}
          playedThisWeek={playedThisWeek}
          leagueSlotTaken={leagueSlotTaken}
          onDrop={(invId) => { const inv = inventory.find(i => i.id === invId); if (inv) openDeploy(inv); }}
          onGoStore={() => setScreen('store')}
        />
      )}

      {/* ---- Detail modal ---- */}
      {modal === 'detail' && focusCard && (() => {
        const r = rarityOf(focusCard.rarity);
        const isLW = focusCard.target_scope === 'league';
        const owned = ownedUnusedByCard[focusCard.id];
        const spent = deployedCardIds.has(focusCard.id);
        return (
          <Scrim onClose={() => setModal('none')}>
            <div className="flex items-start gap-3">
              <ArtChip emoji={focusCard.emoji} r={r} size={72} />
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-1.5">
                  <Badge className={cn('text-[9px] border', RARITY[r].badge)}>{RARITY[r].label}</Badge>
                  {isLW && <Badge className="text-[9px] text-white border-0" style={{ background: 'var(--gradient-sabotage, linear-gradient(135deg,hsl(25 95% 53%),hsl(35 100% 50%)))' }}>LEAGUE-WIDE · 1/WK</Badge>}
                </div>
                <h2 className="font-display font-extrabold text-[23px] leading-tight text-foreground mt-1">{focusCard.name}</h2>
                <p className="font-display font-extrabold text-[26px] text-foreground">{usd(focusCard.price_usd_cents)}</p>
              </div>
            </div>
            <div className="mt-3 rounded-xl bg-background border border-border p-3">
              <p className="text-[10px] font-display font-bold uppercase tracking-wider text-muted-foreground mb-1">The damage</p>
              <p className="text-sm text-foreground">{focusCard.description}</p>
            </div>
            <p className="mt-3 flex items-start gap-2 text-xs text-primary">
              <Clock className="h-4 w-4 shrink-0 mt-0.5" />
              {isLW
                ? <span>Hits the entire league this week. One league-wide Legendary per league per week; rolls over if the slot is taken.</span>
                : <span>Valid only for Week {currentWeek} vs {opponentName}. Expires in {shortExpiry(lockAt)}. One target, single use.</span>}
            </p>
            <div className="mt-4 space-y-2">
              {spent ? (
                <div className="rounded-xl bg-damage/10 border border-damage/30 text-damage text-sm text-center py-2.5 font-medium">Dropped this week · spent</div>
              ) : owned ? (
                <button onClick={() => openDeploy(owned)} className="w-full h-11 rounded-xl border border-primary/50 text-primary font-display font-bold hover:bg-primary/10">
                  Already yours. Drop it now
                </button>
              ) : (
                <button
                  onClick={() => { if (isDemoMode) { navigate('/'); return; } setModal('confirm'); }}
                  className="w-full h-11 rounded-xl text-white font-display font-bold shadow-[0_8px_24px_hsl(25,95%,45%/0.35)]"
                  style={{ background: 'var(--gradient-sabotage, linear-gradient(135deg,hsl(25 95% 53%),hsl(35 100% 50%)))' }}
                >
                  {isDemoMode ? 'Sign up to buy' : `Buy for ${usd(focusCard.price_usd_cents)}`}
                </button>
              )}
              <button onClick={() => setModal('none')} className="w-full text-sm text-muted-foreground hover:text-foreground py-1">Close</button>
            </div>
          </Scrim>
        );
      })()}

      {/* ---- Confirm modal (wallet-only) ---- */}
      {modal === 'confirm' && focusCard && (() => {
        const r = rarityOf(focusCard.rarity);
        const isLW = focusCard.target_scope === 'league';
        const price = focusCard.price_usd_cents ?? 0;
        const enough = walletBalance * 100 >= price;
        return (
          <Scrim onClose={() => setModal('none')}>
            <div className="text-center">
              <h2 className="font-display font-extrabold text-xl text-foreground">Confirm purchase</h2>
              <p className="text-sm text-damage font-medium">Real money. You're charged now.</p>
            </div>
            <div className="mt-4 rounded-xl bg-background border border-border p-3 flex items-center gap-3">
              <ArtChip emoji={focusCard.emoji} r={r} size={44} />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-foreground truncate">{focusCard.name}</p>
                <p className="text-[11px] text-muted-foreground">{RARITY[r].label} · {isLW ? 'League-wide this week' : `Week ${currentWeek} vs ${opponentName}`}</p>
              </div>
              <p className="font-display font-extrabold text-[22px] text-foreground">{usd(price)}</p>
            </div>
            <p className="mt-3 flex items-start gap-2 text-xs text-damage">
              <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
              {isLW ? <span>League-wide, once per week. No refund once played.</span>
                : <span>Only works for <b>this matchup</b> · expires in {shortExpiry(lockAt)}. No refund once the matchup locks.</span>}
            </p>
            <p className="mt-4 text-[10px] font-display font-bold uppercase tracking-wider text-muted-foreground">Pay with</p>
            <div className={cn('mt-2 rounded-xl border p-3 flex items-center gap-3', enough ? 'border-primary bg-primary/[0.08]' : 'border-border')}>
              <span className="h-9 w-9 rounded-lg bg-muted flex items-center justify-center"><Wallet className={cn('h-4 w-4', enough ? 'text-primary' : 'text-muted-foreground')} /></span>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-foreground">Sabotage wallet</p>
                <p className={cn('text-[11px]', enough ? 'text-muted-foreground' : 'text-damage')}>
                  Balance ${walletBalance.toFixed(2)}{!enough && ', not enough'}
                </p>
              </div>
              {enough && <CheckCircle2 className="h-5 w-5 text-primary shrink-0" />}
            </div>
            <div className="mt-4 space-y-2">
              {enough ? (
                <button onClick={handleBuy} disabled={busy} className="w-full h-11 rounded-xl text-white font-display font-bold shadow-[0_8px_24px_hsl(25,95%,45%/0.35)] disabled:opacity-60 flex items-center justify-center" style={{ background: 'var(--gradient-sabotage, linear-gradient(135deg,hsl(25 95% 53%),hsl(35 100% 50%)))' }}>
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : `Pay ${usd(price)}`}
                </button>
              ) : (
                <button onClick={() => navigate('/nfl/settings')} className="w-full h-11 rounded-xl border border-primary/50 text-primary font-display font-bold hover:bg-primary/10">Add funds to wallet</button>
              )}
              <button onClick={() => setModal('detail')} className="w-full text-sm text-muted-foreground hover:text-foreground py-1">Cancel</button>
            </div>
          </Scrim>
        );
      })()}

      {/* ---- Purchase success (SAB-597: opaque Scrim so the store grid can't show
             through a real-money receipt; amount paid + new balance are the hero) ---- */}
      {modal === 'success' && receipt && (
        <Scrim onClose={() => { setScreen('store'); setModal('none'); }}>
          <div className="text-center">
            <div className="mx-auto h-[88px] w-[88px] rounded-2xl flex items-center justify-center text-5xl border-2 animate-in zoom-in-50 duration-500" style={{ borderColor: 'hsl(25 95% 53% / 0.5)', background: 'hsl(25 95% 53% / 0.1)', boxShadow: '0 0 40px hsl(25 95% 53% / 0.35)' }}>{receipt.emoji || '💣'}</div>
            <p className="mt-4 text-xs font-display font-bold uppercase tracking-[0.1em] text-primary">Sabotage acquired</p>
            <h2 className="font-display font-extrabold text-2xl text-foreground mt-1">{receipt.name}</h2>
          </div>
          {/* The money is the most prominent text — it was a real-money charge. */}
          <div className="mt-4 rounded-xl bg-background border border-border p-4 text-center">
            <p className="text-[10px] font-display font-bold uppercase tracking-wider text-muted-foreground">Charged to wallet</p>
            <p className="font-display font-extrabold text-[32px] leading-tight text-foreground">{usd(receipt.price)}</p>
            <p className="text-sm text-muted-foreground mt-1">New balance <span className="font-semibold text-foreground">${receipt.balance.toFixed(2)}</span></p>
          </div>
          <p className="mt-3 text-[13px] text-muted-foreground text-center">Armed for Week {currentWeek} vs {opponentName}. Now go ruin someone's Sunday.</p>
          <div className="mt-4 space-y-2">
            <button onClick={() => { setScreen('arsenal'); setModal('none'); }} className="w-full h-11 rounded-xl text-white font-display font-bold" style={{ background: 'var(--gradient-sabotage, linear-gradient(135deg,hsl(25 95% 53%),hsl(35 100% 50%)))' }}>Drop it now</button>
            <button onClick={() => { setScreen('store'); setModal('none'); }} className="w-full h-11 rounded-xl border border-border text-foreground font-medium">Keep shopping</button>
          </div>
        </Scrim>
      )}

      {/* ---- Deploy flow (shared dialog: target select + success) ---- */}
      <SabotageDeployDialog ctx={deployCtx} onClose={() => setDeployCtx(null)} onDeployed={() => { setScreen('arsenal'); refreshSabotages(); }} />
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Store view                                                       */
/* ------------------------------------------------------------------ */

function StoreView({
  storeByRarity, currentWeek, opponentName, countdown, weeklyRemaining, playedThisWeek,
  ownedUnusedByCard, deployedCardIds, onOpen,
}: {
  storeByRarity: Record<RarityKey, SabotageCard[]>;
  currentWeek: number; opponentName: string; countdown: string;
  weeklyRemaining: number; playedThisWeek: boolean;
  ownedUnusedByCard: Record<string, InventoryItem>; deployedCardIds: Set<string>;
  onOpen: (id: string) => void;
}) {
  return (
    <div className="space-y-6">
      {/* Matchup banner */}
      <div className="relative overflow-hidden rounded-xl border p-5 border-[hsl(25,95%,53%/0.3)]" style={{ background: 'var(--gradient-card, linear-gradient(135deg,hsl(222 47% 10%),hsl(222 47% 6%)))' }}>
        <div className="absolute -top-10 -right-10 h-32 w-32 rounded-full bg-primary/20 blur-3xl" />
        <div className="absolute -bottom-10 -left-10 h-32 w-32 rounded-full bg-damage/20 blur-3xl" />
        <div className="relative flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <p className="text-[10px] font-display font-bold uppercase tracking-[0.1em] text-muted-foreground">Week {currentWeek} matchup · shopping for</p>
            <h2 className="font-display font-extrabold text-2xl text-foreground leading-tight mt-0.5">You <span className="text-muted-foreground">vs</span> {opponentName}</h2>
          </div>
          <div className="text-right shrink-0">
            <span className="inline-flex items-center gap-1.5 text-[10px] font-display font-bold uppercase text-damage"><span className="h-1.5 w-1.5 rounded-full bg-damage animate-pulse" /> Store closes in</span>
            <p className="font-mono text-lg text-foreground tabular-nums leading-tight">{countdown}</p>
            <p className="text-[10px] text-muted-foreground">closes when your matchup locks</p>
          </div>
        </div>
        <div className="relative mt-4 pt-3 border-t border-border/60">
          <div className="h-1.5 rounded-full bg-muted overflow-hidden">
            <div className="h-full rounded-full transition-all" style={{ width: playedThisWeek ? '100%' : '0%', background: 'var(--gradient-sabotage, linear-gradient(135deg,hsl(25 95% 53%),hsl(35 100% 50%)))' }} />
          </div>
          <p className="mt-1.5 text-[11px] text-muted-foreground text-right">
            {playedThisWeek ? 'Your weekly sabotage is used' : `${weeklyRemaining} sabotage available this week`}
          </p>
        </div>
      </div>

      {/* Tier lanes */}
      {RARITY_ORDER.map((rk) => {
        const items = storeByRarity[rk];
        if (!items || items.length === 0) return null;
        const cfg = RARITY[rk];
        const price = usd(items[0].price_usd_cents);
        const isLeg = rk === 'legendary';
        return (
          <div key={rk} className="space-y-3">
            <div className="flex items-center gap-2">
              <span className={cn('h-3 w-3 rounded-sm', cfg.swatch)} />
              <span className={cn('font-display font-bold text-sm uppercase tracking-wide', cfg.text)}>{cfg.label}</span>
              <span className="text-xs text-muted-foreground">· {price} each</span>
              <span className="flex-1 h-px bg-border ml-2" />
            </div>
            <div className="grid gap-3" style={{ gridTemplateColumns: isLeg ? 'repeat(auto-fill,minmax(330px,1fr))' : 'repeat(auto-fill,minmax(158px,1fr))' }}>
              {items.map((c) => {
                const owned = !!ownedUnusedByCard[c.id];
                const spent = deployedCardIds.has(c.id);
                const isLW = c.target_scope === 'league';
                return (
                  <button
                    key={c.id}
                    onClick={() => onOpen(c.id)}
                    className={cn('text-left rounded-xl border p-3 transition-transform hover:-translate-y-0.5 active:scale-[0.97]', cfg.border, cfg.tile, cfg.glow, isLeg && 'flex items-center gap-3')}
                  >
                    <ArtChip emoji={c.emoji} r={rk} size={isLeg ? 58 : 40} />
                    <div className={cn('min-w-0', isLeg ? 'flex-1' : 'mt-2')}>
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="font-display font-bold text-sm text-foreground truncate">{c.name}</span>
                        {isLW && <Badge className="text-[8px] text-white border-0" style={{ background: 'var(--gradient-sabotage, linear-gradient(135deg,hsl(25 95% 53%),hsl(35 100% 50%)))' }}>LEAGUE-WIDE · 1/WK</Badge>}
                        {spent ? <Badge className="text-[8px] bg-damage/15 text-damage border-damage/30">DROPPED</Badge>
                          : owned ? <Badge className="text-[8px] bg-primary/15 text-primary border-primary/30">IN ARSENAL</Badge> : null}
                      </div>
                      <p className="text-[11px] text-muted-foreground line-clamp-2 mt-0.5">{c.description}</p>
                      <div className={cn('flex items-center justify-between', isLeg ? 'mt-1' : 'mt-2')}>
                        <span className="font-display font-extrabold text-[17px] text-foreground">{usd(c.price_usd_cents)}</span>
                        <span className="text-[10px] text-muted-foreground">Tap to buy</span>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}

      <p className="text-center text-xs text-muted-foreground max-w-[640px] mx-auto">
        Cards are bought for this week's matchup and expire when it locks. League-wide Legendaries hit the whole league, one per week, and roll over to a future week if the slot is taken.
      </p>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Arsenal view                                                     */
/* ------------------------------------------------------------------ */

function ArsenalView({
  availableInventory, deployedThisWeek, cardMap, teamMap, countdown, playedThisWeek, leagueSlotTaken, onDrop, onGoStore,
}: {
  availableInventory: InventoryItem[]; deployedThisWeek: SabotageEvent[];
  cardMap: Record<string, SabotageCard>; teamMap: Record<string, string>;
  countdown: string; playedThisWeek: boolean; leagueSlotTaken: boolean;
  onDrop: (invId: string) => void; onGoStore: () => void;
}) {
  return (
    <div className="space-y-5">
      {/* Stat tiles */}
      <div className="grid grid-cols-3 gap-3">
        <div className="rounded-xl border border-border bg-card p-3">
          <p className="font-display font-extrabold text-2xl text-primary">{availableInventory.length}</p>
          <p className="text-[11px] text-muted-foreground">ready to drop</p>
        </div>
        <div className="rounded-xl border border-border bg-card p-3">
          <p className="font-display font-extrabold text-2xl text-damage">{playedThisWeek ? 1 : 0}<span className="text-sm text-muted-foreground">/1</span></p>
          <p className="text-[11px] text-muted-foreground">used this week</p>
        </div>
        <div className="rounded-xl border border-border bg-card p-3">
          <p className="font-mono font-bold text-lg text-foreground tabular-nums leading-tight">{countdown}</p>
          <p className="text-[11px] text-muted-foreground">until they expire</p>
        </div>
      </div>

      {/* Ready to deploy */}
      <section>
        <h3 className="text-[11px] font-display font-bold uppercase tracking-wider text-muted-foreground mb-2">Ready to deploy</h3>
        {availableInventory.length === 0 ? (
          <button onClick={onGoStore} className="w-full rounded-xl border border-dashed border-border p-8 flex flex-col items-center text-center text-muted-foreground hover:text-foreground">
            <ShoppingCart className="h-8 w-8 mb-2" />
            <p className="text-sm">No cards armed</p>
            <p className="text-xs mt-0.5 text-primary">Visit the store</p>
          </button>
        ) : (
          <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fill,minmax(340px,1fr))' }}>
            {availableInventory.map((inv) => {
              const card = cardMap[inv.card_id];
              if (!card) return null;
              const rk = rarityOf(card.rarity);
              const isLW = card.target_scope === 'league';
              const willRoll = isLW && leagueSlotTaken;
              return (
                <div key={inv.id} className={cn('rounded-xl border p-3 flex items-center gap-3', RARITY[rk].border, RARITY[rk].tile)}>
                  <ArtChip emoji={card.emoji} r={rk} size={44} />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5">
                      <span className="font-display font-bold text-sm text-foreground truncate">{card.name}</span>
                      <span className={cn('text-[10px] font-bold', RARITY[rk].text)}>{RARITY[rk].label}</span>
                    </div>
                    <p className="text-[11px] text-muted-foreground line-clamp-1 mt-0.5">{card.description}</p>
                    <p className="text-[10px] text-muted-foreground mt-0.5">
                      {isLW ? (willRoll ? 'League slot taken, rolls to next week' : 'League-wide · 1 per week')
                        : `Locked to this matchup · expires ${shortExpiry(inv.expires_at)}`}
                    </p>
                  </div>
                  <button onClick={() => onDrop(inv.id)} className="shrink-0 h-9 px-3 rounded-lg text-white text-[11px] font-display font-bold flex items-center gap-1" style={{ background: 'var(--gradient-sabotage, linear-gradient(135deg,hsl(25 95% 53%),hsl(35 100% 50%)))' }}>
                    <Target className="h-3.5 w-3.5" /> DROP
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* Dropped this week */}
      <section>
        <h3 className="text-[11px] font-display font-bold uppercase tracking-wider text-muted-foreground mb-2">Dropped this week · your whole league sees these</h3>
        {deployedThisWeek.length === 0 ? (
          <div className="rounded-xl border border-dashed border-border p-4 text-center text-xs text-muted-foreground">Nothing dropped yet. Your opponent sleeps easy.</div>
        ) : (
          <div className="space-y-2">
            {deployedThisWeek.map((ev) => {
              const card = cardMap[ev.card_id];
              return (
                <div key={ev.id} className="rounded-xl border border-damage/30 bg-damage/[0.06] p-3 flex items-center gap-3">
                  <ArtChip emoji={card?.emoji ?? null} r={rarityOf(card?.rarity || 'common')} size={40} />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-foreground truncate">{card?.name || 'Unknown'}</p>
                    <p className="text-[11px] text-muted-foreground flex items-center gap-1">
                      <Clock className="h-3 w-3" /> Dropped on <b className="text-foreground">{ev.target_team_id ? teamMap[ev.target_team_id] || '?' : 'the league'}</b>
                    </p>
                  </div>
                  <Badge className="text-[8px] bg-damage/15 text-damage border-damage/30">LIVE · PUBLIC</Badge>
                </div>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}
